import React from 'react';
import { Check, Loader2, X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import type { Organization } from '@app-types/index';
import type { PendingOrganizationInvitation } from '@hooks/useOrganizationInvitations';

interface OrganizationInvitationRowsProps {
  invitations: PendingOrganizationInvitation[];
  busyInvitationId?: string | null;
  onAccept: (invitationId: string) => Promise<Organization | null>;
  onDecline: (invitationId: string) => Promise<boolean>;
  onCompleted?: () => void;
}

const initials = (name?: string): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?';

const OrganizationInvitationRows: React.FC<OrganizationInvitationRowsProps> = ({
  invitations,
  busyInvitationId,
  onAccept,
  onDecline,
  onCompleted,
}) => (
  <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
    {invitations.map((invitation) => {
      const busy = busyInvitationId === invitation.invitationId;
      return (
        <div
          key={invitation.invitationId}
          className="flex items-center gap-2 rounded-lg bg-marble-blue-50 p-1.5"
        >
          <Avatar size="sm">
            <AvatarImage
              src={invitation.organizationAvatar?.url ?? undefined}
            />
            <AvatarFallback>
              {initials(invitation.organizationName)}
            </AvatarFallback>
          </Avatar>
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-marble-bg-700">
            {invitation.organizationName}
          </p>
          <button
            type="button"
            title="Accept invitation"
            aria-label={`Accept invitation to ${invitation.organizationName}`}
            disabled={busy}
            className="flex size-7 cursor-pointer items-center justify-center rounded-full bg-marble-blue-500 text-white disabled:cursor-wait disabled:opacity-60"
            onClick={() => {
              void onAccept(invitation.invitationId).then((organization) => {
                if (organization) onCompleted?.();
              });
            }}
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
          </button>
          <button
            type="button"
            title="Decline invitation"
            aria-label={`Decline invitation to ${invitation.organizationName}`}
            disabled={busy}
            className="flex size-7 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-marble-bg-600 disabled:cursor-wait disabled:opacity-60"
            onClick={() => {
              void onDecline(invitation.invitationId).then((declined) => {
                if (declined) onCompleted?.();
              });
            }}
          >
            <X className="size-3.5" />
          </button>
        </div>
      );
    })}
  </div>
);

export default OrganizationInvitationRows;
